import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-toastify';
import * as z from 'zod';
import { VerifyIcon } from '../../../assets/Icon';
import WalletContext from '../../../contexts/WalletContext';
import useAPI from '../../../hooks/useAPI';

import Loader from '../../common/Loader';
import Modal from '../../common/modal/Modal';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import TransactionSuccessModal from '../transaction-success-dialog';
import styles from './style.module.scss';
import { emailPattern } from '../../../constants/patterns';
import TokenListContext from '../../../contexts/TokenListContext';
import AuthContext from '../../../contexts/AuthContext';
import BulkTokenTransfer from './BulkTokenTransfer';

type SendCashModalProps = {
  open: boolean;
  onClose: () => void;
};

const schema = z.object({
  amount: z
    .string()
    .min(1, 'Total tokens must be greater than 0')
    .refine(val => Number(val) !== 0, {
      message: 'Enter a valid number of tokens',
    }),
  mobile: z.string().refine(value => {
    return emailPattern.test(value);
  }, 'Please enter a valid email'),
});

const SendCashModal = ({ onClose, open }: SendCashModalProps) => {
  const [cashSend, setCashSend] = useState(false);
  const [receiverData, setReciverData] = useState<any>(null);
  const { userData: authUserData } = useContext(AuthContext);
  const [verifyError, setVerifyError] = useState('');
  const [activeTab, setActiveTab] = useState<'Individual' | 'Bulk'>(
    'Individual',
  );
  const { refetchTransactions, refetch: walletBalanceRefetch } =
    useContext(WalletContext);
  const { tokenList } = useContext(TokenListContext);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control,
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: 'all',
  });

  const { refetch, data, isLoading } = useAPI({
    url: '/token/user/transfer',
    method: 'POST',
    onSuccess: data => {
      refetchTransactions();
      walletBalanceRefetch();
      setCashSend(true);
      setReciverData(null);
      reset();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'An error occurred');
      reset();
      onClose();
    },
  });

  const { refetch: findUser, isLoading: isLoadingMembers } = useAPI({
    url: '/auth/email/find',
    method: 'POST',
    enabled: !!watch('mobile'),
    onSuccess: data => {
      setReciverData(data?.result);
    },
    onError: error => {
      setVerifyError(error?.response?.data?.message || '');
    },
  });

  const onSubmit = (data: FieldValues) => {
    if (tokenList[0]?.tokenId) {
      const payload = {
        amount: data?.amount,
        receiverWallet: receiverData?.wallet[0]?.walletAddress,
      };
      refetch(payload);
    }
  };

  useEffect(() => {
    if (!errors.mobile) {
      const debounceTimer = setTimeout(() => {
        setReciverData(null);
        setVerifyError('');
        findUser({ email: watch('mobile') });
      }, 500);

      return () => clearTimeout(debounceTimer);
    }
  }, [watch('mobile'), errors.mobile]);

  useEffect(() => {
    if (!open) {
      reset();
      setCashSend(false);
      setReciverData(null);
      setActiveTab('Individual');
    }
  }, [open]);
  return (
    <>
      <TransactionSuccessModal
        open={cashSend}
        title={`Token amount ${data?.result?.data?.tokenAmount} sent successfully!`}
        isCloseIconVisible={true}
        onClose={() => {
          setCashSend(false);
          onClose();
          setActiveTab('Individual');
        }}
      />
      {!cashSend && (
        <Modal
          open={open}
          title="Send Token"
          onClose={onClose}
          isCloseIconVisible={!isLoading}
        >
          {authUserData?.role === 'ORGANIZATION' && (
            <div className={styles.tabContainer}>
              <button
                type="button"
                className={`${styles.tabButton} ${activeTab === 'Individual' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('Individual')}
              >
                Individual
              </button>
              <button
                type="button"
                className={`${styles.tabButton} ${activeTab === 'Bulk' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('Bulk')}
              >
                Bulk
              </button>
            </div>
          )}

          {activeTab === 'Individual' && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="" className={styles.label}>
                  Enter Amount <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="amount"
                  control={control}
                  render={({ field }) => (
                    <div style={{ position: 'relative' }}>
                      <NumericFormat
                        thousandSeparator
                        decimalScale={0}
                        allowNegative={false}
                        customInput={Input}
                        onValueChange={value => field.onChange(value.value)}
                        error={errors.amount?.message as string}
                        style={{
                          paddingRight: tokenList?.[0]?.tokenSymbol
                            ? '60px'
                            : '10px',
                        }}
                      />
                      {tokenList?.[0]?.tokenSymbol && (
                        <span
                          style={{
                            position: 'absolute',
                            right: '15px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#666',
                            fontWeight: 600,
                            pointerEvents: 'none',
                          }}
                        >
                          {tokenList[0].tokenSymbol}
                        </span>
                      )}
                    </div>
                  )}
                />
              </Form.Group>
              <Input
                {...register('mobile')}
                label="Enter Email"
                isRequired
                id="mobile"
                error={errors.mobile?.message as string}
              />
              {isLoadingMembers ? (
                <Loader />
              ) : watch('mobile') ? (
                <div
                  className={`p-3 rounded my-2 ${receiverData?.isverify !== '1' || errors.mobile?.message || verifyError ? styles.error : styles.success} border-${receiverData?.isverify !== '1' || verifyError ? 'danger' : 'success'} d-flex justify-content-between align-items-center`}
                >
                  <div>
                    <h6 className="mb-0 fw-bold">{watch('mobile')}</h6>
                    {verifyError && <span>{verifyError}</span>}
                  </div>

                  {receiverData ? (
                    receiverData?.isverify === '1' &&
                    !errors.mobile?.message ? (
                      <div className="d-flex gap-2">
                        <VerifyIcon />
                        <span className="fw-bold text-black">Verified</span>
                      </div>
                    ) : (
                      <div className="d-flex gap-2">
                        <VerifyIcon color="red" />
                        <span className="fw-bold text-black">Unverified</span>
                      </div>
                    )
                  ) : null}
                </div>
              ) : null}
              <div className={styles.actions}>
                <Button
                  variant="outline"
                  onClick={() => {
                    onClose();
                    reset();
                  }}
                  disabled={isLoading}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  disabled={
                    (receiverData ? receiverData.isVerified == '0' : true) ||
                    isLoading ||
                    !!verifyError
                  }
                  variant="dark"
                  type="submit"
                >
                  {isLoading && <Loader />}Send Token
                </Button>
              </div>
            </form>
          )}

          {authUserData?.role === 'ORGANIZATION' && activeTab === 'Bulk' && (
            <BulkTokenTransfer onClose={onClose} setActiveTab={setActiveTab} />
          )}
        </Modal>
      )}
    </>
  );
};

export default SendCashModal;
